import React from 'react';
import { CheckCircle2, Clock, AlertCircle, MapPin, Calendar, Hash } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import type { KitComponent } from '@/app/components/KittingWorkspace';

interface KittingComponentListProps {
  components: KitComponent[];
  activeIndex: number;
}

const statusIcon = {
  complete: <CheckCircle2 size={16} className="text-primary flex-shrink-0" />,
  inprogress: <Clock size={16} className="text-accent flex-shrink-0" />,
  pending: <AlertCircle size={16} className="text-muted-foreground flex-shrink-0" />,
};

export default function KittingComponentList({
  components,
  activeIndex,
}: KittingComponentListProps) {
  return (
    <div className="card-elevated overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">Kit Components</h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {components.filter((c) => c.status === 'complete').length}/{components.length} items
          </span>
          <StatusBadge status="kitting" label="In Assembly" size="sm" />
        </div>
      </div>

      {/* Column headers */}
      <div className="px-5 py-2 border-b border-border grid grid-cols-12 gap-3 text-2xs font-semibold text-muted-foreground uppercase tracking-wider">
        <div className="col-span-1">#</div>
        <div className="col-span-3">Item</div>
        <div className="col-span-2">Batch / Expiry</div>
        <div className="col-span-2">Location</div>
        <div className="col-span-2">Progress</div>
        <div className="col-span-1">Qty</div>
        <div className="col-span-1">Status</div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-border">
        {components.map((comp, idx) => {
          const isActive = idx === activeIndex;
          const pct = Math.round((comp.scannedQty / comp.requiredQty) * 100);

          return (
            <div
              key={comp.id}
              className={`px-5 py-3.5 grid grid-cols-12 gap-3 items-center transition-colors duration-150 ${
                isActive ? 'active-component-row' : 'row-hover'
              }`}
            >
              {/* Index */}
              <div className="col-span-1 flex items-center gap-1.5">
                {statusIcon[comp.status]}
                <span className="text-xs font-mono-data text-muted-foreground">{String(idx + 1).padStart(2, '0')}</span>
              </div>

              {/* Item */}
              <div className="col-span-3">
                <p className={`text-sm font-semibold leading-tight ${isActive ? 'text-primary' : 'text-foreground'}`}>
                  {comp.itemName}
                </p>
                <p className="text-2xs font-mono-data text-muted-foreground mt-0.5">{comp.itemCode}</p>
              </div>

              {/* Batch / Expiry */}
              <div className="col-span-2">
                <div className="flex items-center gap-1 text-2xs text-muted-foreground mb-0.5">
                  <Hash size={9} />
                  <span className="font-mono-data">{comp.batchSuggested}</span>
                </div>
                <div className="flex items-center gap-1 text-2xs text-muted-foreground">
                  <Calendar size={9} />
                  <span className="font-mono-data">{comp.expiryDate}</span>
                </div>
              </div>

              {/* Location */}
              <div className="col-span-2 flex items-center gap-1">
                <MapPin size={10} className="text-muted-foreground flex-shrink-0" />
                <span className="text-xs font-mono-data text-muted-foreground">{comp.location}</span>
              </div>

              {/* Progress bar */}
              <div className="col-span-2">
                <div className="h-1.5 bg-muted rounded-full overflow-hidden mb-1">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      comp.status === 'complete'
                        ? 'bg-primary'
                        : comp.status === 'inprogress' ?'bg-accent' :'bg-muted-foreground/30'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="text-2xs text-muted-foreground tabular-nums">{pct}%</p>
              </div>

              {/* Qty */}
              <div className="col-span-1">
                <span
                  className={`text-sm font-bold tabular-nums ${
                    comp.status === 'complete'
                      ? 'text-primary'
                      : comp.status === 'inprogress' ?'text-accent' :'text-muted-foreground'
                  }`}
                >
                  {comp.scannedQty}
                </span>
                <span className="text-xs text-muted-foreground">/{comp.requiredQty}</span>
                <p className="text-2xs text-muted-foreground">{comp.uom}</p>
              </div>

              {/* Status badge */}
              <div className="col-span-1">
                <StatusBadge status={comp.status} size="sm" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer summary */}
      <div className="px-5 py-3 border-t border-border bg-muted/30 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-primary" />
            <span className="text-xs text-muted-foreground">
              {components.filter((c) => c.status === 'complete').length} Complete
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-accent" />
            <span className="text-xs text-muted-foreground">
              {components.filter((c) => c.status === 'inprogress').length} In Progress
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-muted-foreground/40" />
            <span className="text-xs text-muted-foreground">
              {components.filter((c) => c.status === 'pending').length} Pending
            </span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Kit Type: <span className="text-foreground font-medium">Trauma Kit — Type B</span>
        </p>
      </div>
    </div>
  );
}